import { Button } from "@/components/ui/button";
import { MarkdownEditor } from "@/components/ui/markdown-editor";
import { ApiError, api } from "@/lib/api";
import { useApiMutation } from "@/lib/use-api-mutation";
import { useDraft } from "@/lib/use-draft";
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";

/**
 * `/projects/:slug/wiki/new` — create a wiki page. Committed to the
 * repository's `wiki/` folder first, then Hub mirrors it.
 */

export const Route = createFileRoute("/_app/projects/$slug/wiki/new")({
  component: WikiNewPage,
});

function WikiNewPage() {
  const { t } = useTranslation();
  const { slug } = Route.useParams();
  const navigate = useNavigate();

  const [title, setTitle] = useDraft(`wiki-new:${slug}:title`);
  const [content, setContent] = useDraft(`wiki-new:${slug}:content`);
  const create = useApiMutation(
    (body: { title: string; content: string }) =>
      api.post<{ slug: string; title: string }>(`/api/projects/${slug}/wiki`, body),
    {
      onSuccess: (page) => {
        setTitle("");
        setContent("");
        return navigate({
          to: "/projects/$slug/wiki/$wikiSlug",
          params: { slug, wikiSlug: page.slug },
        });
      },
      errorMessage: (err) =>
        err instanceof ApiError ? t("wk.createErrorPermission") : t("wk.createError"),
    },
  );

  async function createPage(e: React.FormEvent) {
    e.preventDefault();
    if (!title.trim()) return;
    await create.mutate({ title: title.trim(), content });
  }

  return (
    <div className="max-w-3xl mx-auto">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-heading text-xl font-bold">{t("wk.newTitle")}</h2>
        <Button variant="outline" size="sm" asChild>
          <Link to="/projects/$slug/wiki" params={{ slug }}>
            {t("wk.cancel")}
          </Link>
        </Button>
      </div>

      <div className="rounded-md border bg-card p-6">
        <p className="text-xs text-muted-foreground mb-4">{t("wk.newNote")}</p>
        <form onSubmit={createPage} className="space-y-4">
          <div className="space-y-2">
            <label htmlFor="title" className="text-sm font-medium leading-none">
              {t("wk.titleLabel")} <span className="text-destructive">*</span>
            </label>
            <input
              id="title"
              type="text"
              required
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={t("wk.titlePlaceholder")}
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            />
          </div>

          <div className="space-y-2">
            <span className="text-sm font-medium leading-none block">{t("wk.contentLabel")}</span>
            <MarkdownEditor
              value={content}
              onChange={setContent}
              rows={20}
              autoGrow
              placeholder={t("wk.contentPlaceholder")}
              uploadEndpoint={`/api/projects/${slug}/uploads`}
            />
          </div>

          {create.error && <p className="text-sm text-destructive">{create.error}</p>}

          <div className="flex gap-2">
            <Button type="submit" disabled={create.pending || !title.trim()}>
              {create.pending ? t("wk.creating") : t("wk.createPage")}
            </Button>
            <Button variant="outline" type="button" asChild>
              <Link to="/projects/$slug/wiki" params={{ slug }}>
                {t("wk.cancel")}
              </Link>
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
